/**
 * The big Bin restore's instances of the generic job machinery (#854): its
 * tracker, its in-flight Bin rows and its reload record. How a job is followed
 * — socket first, status route as the safety net — is in `job-tracker.ts`.
 */
import type { OperationProgressEvent } from '@papan/realtime/client'
import {
  createJobTracker, createInFlightSet, createPendingJobs,
  type JobTracker, type JobTrackerDeps, type JobTrackerTimings, type PendingJob,
} from './job-tracker.js'

export interface RestoreJobOutcome {
  /** The restored root. */
  fileId: string
  /** #826: whether it came back under another name; absent from an older worker. */
  renamed?: boolean
}

export interface RestoreJobProgress {
  phase: NonNullable<OperationProgressEvent['phase']>
  done: number
  total: number
}

export type RestoreTrackerDeps = Omit<JobTrackerDeps<RestoreJobOutcome>, 'kind' | 'readResult'>

export interface RestoreTracker extends Omit<JobTracker<RestoreJobOutcome>, 'follow'> {
  follow: (jobId: string, operationId: string, onProgress?: (p: RestoreJobProgress) => void) => Promise<RestoreJobOutcome>
}

/** A completed restore event carries the restored root. */
function readRestoreResult(e: OperationProgressEvent): RestoreJobOutcome | null {
  return e.fileId ? { fileId: e.fileId, ...(typeof e.renamed === 'boolean' ? { renamed: e.renamed } : {}) } : null
}

export function createRestoreTracker(
  deps: RestoreTrackerDeps,
  ownOperationId: string,
  timings: JobTrackerTimings = {},
): RestoreTracker {
  return createJobTracker({ ...deps, kind: 'restore', readResult: readRestoreResult }, ownOperationId, timings) as RestoreTracker
}

/** Bin entries this tab has a Restore request in flight for, keyed on the bin id. */
export const inFlightRestores = createInFlightSet()

export interface PendingRestore extends PendingJob {
  binId: string
  name: string
}

/** Restores a reload interrupted, per tab. */
export const pendingRestores = createPendingJobs<PendingRestore>('papan:pending-restores')
